// packages/front/src/ideas/modals/add-idea-modal.tsx
//
// Add/Edit Idea modal. Talks to /api/ideas via ideas.hooks (TanStack Query).
// Symbol selection reuses the shared SymbolCombobox from _common.

import { useEffect, useState, type FC, type FormEvent } from "react";
import { CURRENCY_LIST } from "@signals/shared";
import { apiFetch, Button, Modal, Select, SymbolCombobox, toDateInput } from "../../_common";
import { useCreateIdea, useUpdateIdea, type Idea } from "../ideas.hooks";

export type { Idea } from "../ideas.hooks";

export interface AddIdeaModalProps {
  open: boolean;
  onClose: () => void;
  editId?: string | undefined;
  initialData?: Partial<Idea> | undefined;
}

const STATUSES = [
  "new",
  "watching",
  "in_position",
  "partial_sold",
  "sold",
  "failed",
];

const inputClass =
  "h-9 rounded-md border border-input bg-background px-3 py-1 text-sm";
const textareaClass =
  "min-h-[80px] rounded-md border border-input bg-background px-3 py-1 text-sm";
const labelClass = "text-sm font-medium";

export const AddIdeaModal: FC<AddIdeaModalProps> = ({
  open,
  onClose,
  editId,
  initialData,
}) => {
  const isEdit = Boolean(editId);
  const createIdea = useCreateIdea();
  const updateIdea = useUpdateIdea();
  const saving = createIdea.isPending || updateIdea.isPending;

  const [symbolId, setSymbolId] = useState("");
  const [symbolLabel, setSymbolLabel] = useState<string | null>(null);
  const [status, setStatus] = useState("new");
  const [strategy, setStrategy] = useState("");
  const [description, setDescription] = useState("");
  const [pros, setPros] = useState("");
  const [cons, setCons] = useState("");
  const [note, setNote] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [symbolComboboxKey, setSymbolComboboxKey] = useState(0);
  const [newSymbolOpen, setNewSymbolOpen] = useState(false);
  const [newSymbol, setNewSymbol] = useState("");
  const [newSymbolName, setNewSymbolName] = useState("");
  const [newSymbolCurrency, setNewSymbolCurrency] = useState("USD");
  const [newSymbolError, setNewSymbolError] = useState<string | null>(null);

  // Seed form from initialData when opening in edit mode
  useEffect(() => {
    if (!open || !initialData) return;
    if (initialData.symbolId !== undefined) {
      setSymbolId(initialData.symbolId);
      const ticker = initialData.symbolTicker ?? "";
      const name = initialData.symbolName ?? "";
      setSymbolLabel(ticker ? (name ? `${ticker} — ${name}` : ticker) : null);
    }
    if (initialData.status !== undefined) setStatus(initialData.status);
    if (initialData.strategy !== undefined) setStrategy(initialData.strategy ?? "");
    if (initialData.description !== undefined) setDescription(initialData.description ?? "");
    if (initialData.pros !== undefined) setPros(initialData.pros ?? "");
    if (initialData.cons !== undefined) setCons(initialData.cons ?? "");
    if (initialData.note !== undefined) setNote(initialData.note ?? "");
    if (initialData.date !== undefined) setDate(toDateInput(initialData.date));
  }, [open, initialData]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const payload: Idea = {
      symbolId,
      status,
      description,
      date,
    };
    if (strategy) payload["strategy"] = strategy;
    if (pros) payload["pros"] = pros;
    if (cons) payload["cons"] = cons;
    if (note) payload["note"] = note;
    try {
      if (isEdit && editId) {
        await updateIdea.mutateAsync({ id: editId, body: payload });
      } else {
        await createIdea.mutateAsync(payload);
      }
      onClose();
    } catch {
      // error available on mutation .error
    }
  };

  const handleCreateSymbol = async (e: FormEvent) => {
    e.preventDefault();
    setNewSymbolError(null);
    try {
      const response = await apiFetch<{
        data: { id: string; symbol: string; name: string | null };
      }>("/api/symbols", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          symbol: newSymbol.trim().toUpperCase(),
          name: newSymbolName.trim() || newSymbol.trim().toUpperCase(),
          currency: newSymbolCurrency,
        }),
      });
      const created = response.data;
      setSymbolId(created.id);
      setSymbolLabel(
        created.name ? `${created.symbol} — ${created.name}` : created.symbol,
      );
      setSymbolComboboxKey((key) => key + 1);
      setNewSymbolOpen(false);
      setNewSymbol("");
      setNewSymbolName("");
    } catch (error) {
      setNewSymbolError(
        error instanceof Error ? error.message : "Failed to create symbol",
      );
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? "Edit Idea" : "Add Idea"}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <label className={labelClass}>Symbol</label>
        <SymbolCombobox
          key={symbolComboboxKey}
          value={symbolId}
          onChange={setSymbolId}
          initialLabel={symbolLabel ?? undefined}
        />
        {!isEdit && (
          <Button
            type="button"
            variant="outline"
            onClick={() => setNewSymbolOpen(true)}
          >
            + New Symbol
          </Button>
        )}

        <label className={labelClass}>Status</label>
        <Select
          className={inputClass}
          value={status}
          onChange={setStatus}
          options={STATUSES.map((s) => ({ value: s, label: s }))}
          ariaLabel="Status"
        />

        <label className={labelClass}>Strategy</label>
        <input
          className={inputClass}
          value={strategy}
          onChange={(e) => setStrategy(e.target.value)}
        />

        <label className={labelClass}>Description</label>
        <textarea
          className={textareaClass}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          required
          data-testid="idea-description"
        />

        <label className={labelClass}>Pros</label>
        <input
          className={inputClass}
          value={pros}
          onChange={(e) => setPros(e.target.value)}
        />

        <label className={labelClass}>Cons</label>
        <input
          className={inputClass}
          value={cons}
          onChange={(e) => setCons(e.target.value)}
        />

        <label className={labelClass}>Note</label>
        <input
          className={inputClass}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />

        <label className={labelClass}>Date</label>
        <input
          className={inputClass}
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />

        <div className="mt-2 flex gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </form>
      <Modal
        open={newSymbolOpen}
        onClose={() => setNewSymbolOpen(false)}
        title="New Symbol"
      >
        <form onSubmit={handleCreateSymbol} className="flex flex-col gap-3">
          <label className={labelClass}>Symbol (Ticker)</label>
          <input
            className={inputClass}
            value={newSymbol}
            onChange={(event) => setNewSymbol(event.target.value)}
            required
          />
          <label className={labelClass}>Name</label>
          <input
            className={inputClass}
            value={newSymbolName}
            onChange={(event) => setNewSymbolName(event.target.value)}
          />
          <label className={labelClass}>Currency</label>
          <Select
            className={inputClass}
            value={newSymbolCurrency}
            onChange={setNewSymbolCurrency}
            options={CURRENCY_LIST.map((currency) => ({
              value: currency,
              label: currency,
            }))}
            ariaLabel="Currency"
          />
          {newSymbolError && (
            <p role="alert" className="text-sm text-destructive">
              {newSymbolError}
            </p>
          )}
          <div className="mt-2 flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setNewSymbolOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit">Create Symbol</Button>
          </div>
        </form>
      </Modal>
    </Modal>
  );
};
